import React, { useState } from 'react';
import {
  Eye,
  EyeOff,
  Users,
  Lock,
  Search,
  Target,
  Zap,
  Network,
  Layers,
  MessageSquare,
  Map,
  Settings,
  Gauge,
  BookOpen
} from 'lucide-react';

export const JohariWindowM2Model: React.FC = () => {
  const [selectedFormat, setSelectedFormat] = useState<'slide' | 'handout' | 'field-card' | 'poster' | 'mobile'>('slide');
  const [selectedContext, setSelectedContext] = useState<'coaching' | 'educational' | 'organizational' | 'personal'>('coaching');

  // M3 Metadata for Johari Window
  const m3Metadata = {
    version: 3,
    isEmbedded: true,
    ontology: {
      categories: ['Framework', 'Tool', 'Method'],
      structures: ['Matrix', '2D Grid'],
      relationships: {
        'is-a': ['Framework', 'Self-Awareness Tool', 'Communication Aid'],
        'part-of': ['Coaching Toolkit', 'Personal Growth Framework', 'Peak Priorities Base Camp'],
        'applies-to': ['Self-Disclosure', 'Feedback', 'Team Building', 'Leadership Development'],
        'used-by': ['Coaches', 'Leaders', 'Teams', 'Individuals']
      },
      systems: ['Peak Priorities Base Camp', 'Coaching Toolkit', 'Communication System']
    },
    modality: {
      attributes: {
        'Impact/Value': 7,
        'Effort/Complexity': 5,
        'Clarity/Ambiguity': 8,
        'Learning Value': 8,
        'Energy/Motivation': 6
      },
      states: {
        'Development Status': 'Ready',
        'Readiness': 'Ready',
        'Health': 'Green',
        'Confidence': 7
      },
      contexts: {
        'Scope': 'Individual',
        'Environment': 'Calm',
        'Domain': 'Coaching',
        'Temporal': 'Ongoing'
      }
    },
    dynamics: {
      rules: ['Feedback Requirement', 'Self-Disclosure Balance', 'Trust Building'],
      processes: ['Feedback Loop', 'Self-Disclosure Process', 'Blind Spot Reduction'],
      causality: ['Direct Cause → Effect', 'Feedback Loops', 'Trust → Disclosure']
    },
    evaluation: {
      intent: {
        'Actor Scope': 'Individual',
        'Stakeholders': 'Coachee',
        'Time Horizon': 'Mid-term',
        'Motivational Frame': 'Intrinsic'
      },
      utility: {
        'Outcome Speed': 6,
        'Resilience': 7,
        'Clarity/Coherence': 8,
        'Learning Value': 8,
        'Customer Value': 7
      }
    }
  };

  // Visual Signature Elements (shared with Ladder of Inference)
  const visualSignatures = {
    colorCoding: {
      primary: 'primary', // Framework
      secondary: 'sage',  // Health/Ready
      accent: 'amber'     // High Learning Value
    },
    iconBadges: [
      { icon: <Layers className="h-4 w-4" />, label: 'Framework', color: 'primary' },
      { icon: <MessageSquare className="h-4 w-4" />, label: 'Coaching', color: 'sage' },
      { icon: <Target className="h-4 w-4" />, label: 'Individual', color: 'amber' }
    ],
    statusIndicators: [
      { type: 'dot', color: 'sage', label: 'Ready' },
      { type: 'flag', color: 'sage', label: 'Healthy' }
    ],
    metadataBars: [
      { label: 'Impact', value: 7, max: 10, color: 'primary' },
      { label: 'Learning', value: 8, max: 10, color: 'amber' },
      { label: 'Clarity', value: 8, max: 10, color: 'sage' }
    ]
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary':
        return 'bg-consulting-slate-blue-100 border-consulting-slate-blue-300 text-consulting-slate-blue-800';
      case 'sage':
        return 'bg-sage-100 border-sage-300 text-sage-800';
      case 'amber':
        return 'bg-[var(--mustard-dim)] border-[var(--mustard)] text-[var(--mustard)]';
      case 'luminous-coral':
        return 'bg-luminous-coral-100 border-luminous-coral-300 text-luminous-coral-800';
      default:
        return 'bg-[var(--hover-tint)] border-[var(--rule)] text-[var(--text-pri)]';
    }
  };

  const renderJohariWindow = () => (
    <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
      {/* Open Area */}
      <div className={`p-4  border-2 ${getColorClasses('primary')} text-center`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Eye className="h-5 w-5" />
          <span className="font-semibold text-sm">Open Self</span>
        </div>
        <div className="text-xs opacity-75">
          Known to self<br />
          Known to others
        </div>
        <div className="text-xs mt-2 font-medium">
          Arena
        </div>
      </div>

      {/* Blind Spot */}
      <div className={`p-4  border-2 ${getColorClasses('sage')} text-center`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <EyeOff className="h-5 w-5" />
          <span className="font-semibold text-sm">Blind Self</span>
        </div>
        <div className="text-xs opacity-75">
          Unknown to self<br />
          Known to others
        </div>
        <div className="text-xs mt-2 font-medium">
          Blind Spot
        </div>
      </div>

      {/* Hidden Area */}
      <div className={`p-4  border-2 ${getColorClasses('amber')} text-center`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Lock className="h-5 w-5" />
          <span className="font-semibold text-sm">Hidden Self</span>
        </div>
        <div className="text-xs opacity-75">
          Known to self<br />
          Unknown to others
        </div>
        <div className="text-xs mt-2 font-medium">
          Facade
        </div>
      </div>

      {/* Unknown Area */}
      <div className={`p-4  border-2 ${getColorClasses('luminous-coral')} text-center`}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <Search className="h-5 w-5" />
          <span className="font-semibold text-sm">Unknown Self</span>
        </div>
        <div className="text-xs opacity-75">
          Unknown to self<br />
          Unknown to others
        </div>
        <div className="text-xs mt-2 font-medium">
          Unknown
        </div>
      </div>
    </div>
  );

  const renderVisualSignatures = () => (
    <div className="space-y-4">
      {/* Icon Badges */}
      <div className="flex items-center gap-2">
        {visualSignatures.iconBadges.map((badge, index) => (
          <div key={index} className={`px-2 py-1 rounded-full border text-xs flex items-center gap-1 ${getColorClasses(badge.color)}`}>
            {badge.icon}
            <span>{badge.label}</span>
          </div>
        ))}
        {visualSignatures.statusIndicators.map((indicator, index) => (
          <div key={index} className={`w-3 h-3 rounded-full ${indicator.color === 'sage' ? 'bg-sage-500' : 'bg-[var(--mustard)]'}`} title={indicator.label}></div>
        ))}
      </div>

      {/* Metadata Bars */}
      <div className="space-y-2">
        {visualSignatures.metadataBars.map((bar, index) => (
          <div key={index} className="flex items-center gap-2">
            <span className="text-xs font-medium w-16">{bar.label}</span>
            <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${bar.color === 'primary' ? 'bg-consulting-slate-blue-600' : bar.color === 'amber' ? 'bg-[var(--mustard)]' : 'bg-sage-600'}`}
                style={{ width: `${(bar.value / bar.max) * 100}%` }}
              ></div>
            </div>
            <span className="text-xs w-6">{bar.value}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderFormatSpecific = () => {
    switch (selectedFormat) {
      case 'slide':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">The Johari Window</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Framework</h3>
                {renderJohariWindow()}
              </div>
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Key Insights</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-sage-50 border border-sage-200">
                    <div className="text-sm font-medium text-sage-800">Feedback Requirement</div>
                    <div className="text-xs text-sage-700">Ask others what they see in you</div>
                  </div>
                  <div className="p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
                    <div className="text-sm font-medium text-[var(--mustard)]">High Learning Value</div>
                    <div className="text-xs text-[var(--mustard)]">Reveals blind spots and hidden strengths</div>
                  </div>
                </div>
                {renderVisualSignatures()}
              </div>
            </div>
          </div>
        );

      case 'handout':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Johari Window: Know and Share Yourself With Skill</h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">The Framework</h3>
                  {renderJohariWindow()}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">M3 Metadata</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium text-charcoal-700">Categories</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {m3Metadata.ontology.categories.map((category, index) => (
                          <span key={index} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs">
                            {category}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-charcoal-700">Key Attributes</div>
                      <div className="space-y-1 mt-1">
                        {Object.entries(m3Metadata.modality.attributes).slice(0, 3).map(([key, value]) => (
                          <div key={key} className="flex items-center gap-2">
                            <span className="text-xs w-20">{key}</span>
                            <div className="flex-1 bg-[var(--hover-tint)] rounded-full h-1">
                              <div 
                                className="bg-consulting-slate-blue-600 h-1 rounded-full"
                                style={{ width: `${(value / 10) * 100}%` }}
                              ></div>
                            </div>
                            <span className="text-xs w-4">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Visual Signatures</h3>
                {renderVisualSignatures()}
              </div>
            </div>
          </div>
        );

      case 'field-card':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-6">
            <div className="space-y-4">
              {/* Compact Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-charcoal-900">Johari Window</h2>
                <div className="flex items-center gap-1">
                  {visualSignatures.iconBadges.slice(0, 2).map((badge, index) => (
                    <div key={index} className={`w-6 h-6 rounded-full border flex items-center justify-center ${getColorClasses(badge.color)}`}>
                      {badge.icon}
                    </div>
                  ))}
                </div>
              </div>

              {/* Compact Johari Window */}
              <div className="grid grid-cols-2 gap-2">
                <div className={`p-2 rounded border-l-4 ${getColorClasses('primary')} text-center`}>
                  <div className="text-xs font-medium">Open</div>
                  <div className="text-xs opacity-75">Known to all</div>
                </div>
                <div className={`p-2 rounded border-l-4 ${getColorClasses('sage')} text-center`}>
                  <div className="text-xs font-medium">Blind</div>
                  <div className="text-xs opacity-75">Others see</div>
                </div>
                <div className={`p-2 rounded border-l-4 ${getColorClasses('amber')} text-center`}>
                  <div className="text-xs font-medium">Hidden</div>
                  <div className="text-xs opacity-75">You know</div>
                </div>
                <div className={`p-2 rounded border-l-4 ${getColorClasses('luminous-coral')} text-center`}>
                  <div className="text-xs font-medium">Unknown</div>
                  <div className="text-xs opacity-75">No one knows</div>
                </div>
              </div>

              {/* Compact Metadata */}
              <div className="flex items-center gap-2 text-xs">
                <span>Impact: {m3Metadata.modality.attributes['Impact/Value']}</span>
                <span>•</span>
                <span>Learning: {m3Metadata.modality.attributes['Learning Value']}</span>
                <span>•</span>
                <span>Ready</span>
              </div>
            </div>
          </div>
        );

      case 'poster':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-3xl font-bold text-charcoal-900 mb-8 text-center">The Johari Window</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2">
                <div className="max-w-lg mx-auto">
                  {renderJohariWindow()}
                </div>
              </div>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Framework Details</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium text-charcoal-700">Categories</div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {m3Metadata.ontology.categories.map((category, index) => (
                          <span key={index} className="px-2 py-1 bg-consulting-slate-blue-100 text-consulting-slate-blue-800 rounded text-xs">
                            {category}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Visual Signatures</h3>
                  {renderVisualSignatures()}
                </div>
              </div>
            </div>
          </div>
        );

      case 'mobile':
        return (
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-4">
            <div className="space-y-3">
              {/* Mobile Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-charcoal-900">Johari Window</h2>
                <div className="flex items-center gap-1">
                  <div className={`w-4 h-4 rounded-full border ${getColorClasses(visualSignatures.colorCoding.primary)}`}></div>
                  <div className={`w-2 h-2 rounded-full ${visualSignatures.statusIndicators[0].color === 'sage' ? 'bg-sage-500' : 'bg-[var(--mustard)]'}`}></div>
                </div>
              </div>

              {/* Mobile Johari Window */}
              <div className="grid grid-cols-2 gap-1">
                {[
                  { label: 'Open', color: visualSignatures.colorCoding.primary },
                  { label: 'Blind', color: visualSignatures.colorCoding.secondary },
                  { label: 'Hidden', color: visualSignatures.colorCoding.accent },
                  { label: 'Unknown', color: 'luminous-coral' }
                ].map((quadrant, index) => (
                  <div key={index} className={`h-4 rounded border-l-2 ${getColorClasses(quadrant.color)}`}></div>
                ))}
              </div>

              {/* Mobile Metadata */}
              <div className="text-xs text-center">
                {m3Metadata.modality.attributes['Impact/Value']}/{m3Metadata.modality.attributes['Learning Value']}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Users className="h-10 w-10 text-consulting-slate-blue-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Johari Window M2 Model</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-4xl mx-auto">
          A concrete M2 model with M3 metadata applied, demonstrating how visual signature elements 
          encode semantic meaning across different artifact formats and contexts.
        </p>
      </div>

      {/* Format and Context Selection */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">Format & Context Selection</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Format</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'slide', name: 'PowerPoint Slide', icon: <Eye className="h-4 w-4" /> },
                { id: 'handout', name: 'Handout', icon: <BookOpen className="h-4 w-4" /> },
                { id: 'field-card', name: 'Field Card', icon: <Target className="h-4 w-4" /> },
                { id: 'poster', name: 'Poster', icon: <Map className="h-4 w-4" /> },
                { id: 'mobile', name: 'Mobile', icon: <Settings className="h-4 w-4" /> }
              ].map((format) => (
                <button
                  key={format.id}
                  onClick={() => setSelectedFormat(format.id as any)}
                  className={`p-3  border-2 transition-all text-left ${
                    selectedFormat === format.id
                      ? 'border-consulting-slate-blue-600 bg-consulting-slate-blue-50 text-consulting-slate-blue-800'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {format.icon}
                    <span className="text-sm font-medium">{format.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Context</h3>
            <div className="space-y-2">
              {[
                { id: 'coaching', name: 'Coaching Professional', icon: <MessageSquare className="h-4 w-4" /> },
                { id: 'educational', name: 'Educational & Teaching', icon: <BookOpen className="h-4 w-4" /> },
                { id: 'organizational', name: 'Organizational Team', icon: <Users className="h-4 w-4" /> },
                { id: 'personal', name: 'Personal Development', icon: <Target className="h-4 w-4" /> }
              ].map((context) => (
                <button
                  key={context.id}
                  onClick={() => setSelectedContext(context.id as any)}
                  className={`p-3  border-2 transition-all text-left w-full ${
                    selectedContext === context.id
                      ? 'border-sage-600 bg-sage-50 text-sage-800'
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] text-charcoal-700 hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {context.icon}
                    <span className="text-sm font-medium">{context.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Format-Specific Rendering */}
      {renderFormatSpecific()}

      {/* M3 Metadata Summary */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6 text-center">M3 Metadata Summary</h2>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Network className="h-5 w-5" />
              Ontology
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Categories:</div>
                <div className="text-charcoal-600">{m3Metadata.ontology.categories.join(', ')}</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Systems:</div>
                <div className="text-charcoal-600">{m3Metadata.ontology.systems.length} systems</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Gauge className="h-5 w-5" />
              Modality
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Impact:</div>
                <div className="text-charcoal-600">{m3Metadata.modality.attributes['Impact/Value']}/10</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Learning:</div>
                <div className="text-charcoal-600">{m3Metadata.modality.attributes['Learning Value']}/10</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Zap className="h-5 w-5" />
              Dynamics
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Rules:</div>
                <div className="text-charcoal-600">{m3Metadata.dynamics.rules.length} active</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Processes:</div>
                <div className="text-charcoal-600">{m3Metadata.dynamics.processes.length} defined</div>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-charcoal-900 flex items-center gap-2">
              <Target className="h-5 w-5" />
              Evaluation
            </h3>
            <div className="space-y-2">
              <div className="text-sm">
                <div className="font-medium">Scope:</div>
                <div className="text-charcoal-600">{m3Metadata.evaluation.intent['Actor Scope']}</div>
              </div>
              <div className="text-sm">
                <div className="font-medium">Horizon:</div>
                <div className="text-charcoal-600">{m3Metadata.evaluation.intent['Time Horizon']}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
